import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, Stack } from "@chakra-ui/react";
import { FieldWrapper } from "../../components/form/FieldWrapper";
import { NullableSwitch } from "../../components/form/NullableSwitch";
import { useDisabled, useNAState, isEmptyValue } from "../../utils/formField";

type Props = {
  field: any;               
  value?: any;              
  readOnly?: any;
  invalid?: boolean;
  submitted?: boolean;
};

const CheckboxField: React.FC<Props> = ({
  field,
  value = [],
  readOnly,
  invalid,
  submitted = false,
}) => {
  const nullable = field?.nullable === true;

  
  const options: string[] = useMemo(
    () => (Array.isArray(field?.options) ? field.options : []),
    [field?.options]
  );

  const isSingle = options.length === 0;

  
  
  const { mode, setMode } = useNAState({
    value: isSingle ? (typeof value === "string" ? value : "") : "",
    nullable,
  });
  const { ro, disabled } = useDisabled({ readOnly, nullable, mode });

  
  
  
  const [selected, setSelected] = useState<string[]>(
    Array.isArray(value)
      ? value
      : typeof value === "string" && value
      ? [value]
      : []
  );

  
  useEffect(() => {
    if (Array.isArray(value)) {
      setSelected(value);
    } else if (typeof value === "string" && value) {
      setSelected([value]);
    } else {
      setSelected([]);
    }
  }, [value]);

  
  const requiredNow = !nullable || mode === "val";

  
  const derivedInvalid = !!invalid || (submitted && requiredNow && isEmptyValue(selected));

  
  const toggleOption = (opt: string, checked: boolean) => {
    setSelected((prev) => {
      if (checked) {
        if (prev.includes(opt)) return prev;
        return [...prev, opt];
      }
      return prev.filter((v) => v !== opt);
    });
  };

  
  
  const nameAttr = !ro && requiredNow ? field.name : undefined;

  return (
    <FieldWrapper field={field} invalid={derivedInvalid}>
      <NullableSwitch
        nullable={nullable}
        mode={mode}
        setMode={setMode}
        readOnly={ro}
      >
        <Stack spacing={2}>
          {isSingle ? (
            <Checkbox
              isChecked={selected.includes("true")}
              onChange={(e) => toggleOption("true", e.target.checked)}
              isDisabled={disabled}
            >
              {field?.singleLabel ?? field?.label ?? "Yes"}
            </Checkbox>
          ) : (
            options.map((opt, idx) => (
              <Checkbox
                key={idx}
                name={nameAttr}
                value={opt}
                isChecked={selected.includes(opt)}
                onChange={(e) => toggleOption(opt, e.target.checked)}
                isDisabled={disabled}
              >
                {opt}
              </Checkbox>
            ))
          )}
        </Stack>
      </NullableSwitch>

      {ro && (
        <>
          {nullable && mode === "na" ? (
            <input type="hidden" name={field.name} value="N/A" />
          ) : isSingle ? (
            selected.includes("true") ? (
              <input type="hidden" name={field.name} value="true" />
            ) : null
          ) : (
            selected.map((v, i) => (
              <input key={i} type="hidden" name={field.name} value={v} />
            ))
          )}
        </>
      )}

      {/* NA esplicito quando non readOnly */}
      {!ro && nullable && mode === "na" && (
        <input type="hidden" name={field.name} value="N/A" />
      )}
    </FieldWrapper>
  );
};

export default CheckboxField;
